import { useEffect, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";

import { getMcapSectionSummary } from "api/api.service";
import DataTable from "components/DataTable/DataTable";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";
import typography from "../typography";

const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };

// Gap 1's per-student roster, surfaced the moment an admin narrows MCAP down
// to one section via the School/Section filter — this is what makes that
// filter more than a query-scope toggle: the aggregate KPIs above narrow,
// and this table shows exactly which students they narrowed to.
export default function ClassroomRollup({ sectionId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sectionId) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getMcapSectionSummary(sectionId)
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sectionId]);

  if (!sectionId) return null;

  return (
    <motion.div initial="hidden" animate="visible" variants={panelVariants}>
      <Card className="mt-3">
        <CardBody>
          <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">
            {typography.CLASSROOM_ROLLUP_TITLE}
            {summary ? ` — ${summary.sectionName} (${summary.schoolName})` : ""}
          </h5>
          {loading ? (
            <LoaderFallbackUI isLoader />
          ) : (
            <DataTable
              data={summary?.students || []}
              loading={false}
              config={[
                {
                  title: typography.STUDENT,
                  render: (row) => <div className="standard-typo-14-400">{row.name || "-"}</div>,
                },
                {
                  title: typography.MOOD_COUNT,
                  render: (row) => <div className="standard-typo-14-400">{row.moodCount}</div>,
                },
                {
                  title: typography.LAST_MOOD,
                  render: (row) => (
                    <div className="standard-typo-14-400">
                      {row.lastMoodAt ? convertDateTime({ date: row.lastMoodAt, customFormat: dateTimeFormat.monthLetterDateFormat }) : "-"}
                    </div>
                  ),
                },
                {
                  title: typography.BLS_COMPLETED,
                  render: (row) => <div className="standard-typo-14-400">{row.blsCompletedCount}</div>,
                },
                {
                  title: typography.LAST_BLS,
                  render: (row) => (
                    <div className="standard-typo-14-400">
                      {row.lastBlsAt ? convertDateTime({ date: row.lastBlsAt, customFormat: dateTimeFormat.monthLetterDateFormat }) : "-"}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
}
